type PaginationProps = {
  current: number;
  total: number;
  onChange: (page: number) => void;
};

function buildPages(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  const pages: (number | "ellipsis")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push("ellipsis");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

export default function Pagination({ current, total, onChange }: PaginationProps) {
  if (total <= 1) return null;

  const pages = buildPages(current, total);
  const buttonBase =
    "focus-ring inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-2.5 text-xs font-medium transition-colors";

  return (
    <nav className="mt-8 flex flex-wrap items-center justify-center gap-2" aria-label="分页导航">
      <button
        type="button"
        onClick={() => onChange(current - 1)}
        disabled={current <= 1}
        className={`${buttonBase} cursor-pointer border-background-300/80 bg-white text-foreground-700 hover:border-secondary-300 hover:text-secondary-700 disabled:cursor-not-allowed disabled:border-background-200 disabled:text-foreground-300`}
        aria-label="上一页"
      >
        <i className="ri-arrow-left-s-line text-base" aria-hidden="true" />
        上一页
      </button>

      {pages.map((page, index) =>
        page === "ellipsis" ? (
          <span
            key={`ellipsis-${index}`}
            className="inline-flex h-9 w-6 items-center justify-center text-xs text-foreground-400"
          >
            ···
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onChange(page)}
            aria-current={page === current ? "page" : undefined}
            className={`${buttonBase} cursor-pointer ${
              page === current
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-300/80 bg-white text-foreground-700 hover:border-secondary-300 hover:text-secondary-700"
            }`}
          >
            {page}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(current + 1)}
        disabled={current >= total}
        className={`${buttonBase} cursor-pointer border-background-300/80 bg-white text-foreground-700 hover:border-secondary-300 hover:text-secondary-700 disabled:cursor-not-allowed disabled:border-background-200 disabled:text-foreground-300`}
        aria-label="下一页"
      >
        下一页
        <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
      </button>
    </nav>
  );
}